<script setup lang="ts">
/**
 * Composant Vue360Viewer - Visualiseur 360° interactif
 * Support multi-layers avec rotation par drag/swipe
 * Basé sur l'analyse de ConfigView.js
 */

import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRotation360, useImagePreloader360 } from '@/composables'
import { LoadingSpinner } from '@/components/common'
import ViewerControls from './ViewerControls.vue'
import type { MaterialLayer } from '@/types'

defineOptions({
  name: 'Vue360Viewer'
})

/**
 * Props du composant
 */
interface Props {
   /** Liste des matériaux/layers à afficher */
   materials: MaterialLayer[]
   /** Nombre total d'images par rotation complète (par défaut: 36) */
   numAngles?: number
   /** Nombre d'angles affichables (par défaut: 12) */
   numAngleStep?: number
   /** Chemin du répertoire contenant les matériaux */
   materialDirPath: string
   /** Angle initial (par défaut: 0) */
   initialAngle?: number
   /** Déclenche une rotation complète 360° */
   trigger360Rotation?: boolean
   /** Afficher les contrôles de navigation */
   showControls?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  numAngles: 36,
  numAngleStep: 36,
  initialAngle: 0,
  trigger360Rotation: false,
  showControls: true
})

/**
 * Événements émis par le composant
 */
interface Emits {
  /** Émis lors du changement d'angle */
  (e: 'angleChange', angle: number): void
  /** Émis lors de la progression du chargement */
  (e: 'loadProgress', progress: number): void
  /** Émis lorsque toutes les images sont chargées */
  (e: 'loadComplete'): void
}

const emit = defineEmits<Emits>()

// Références
const viewerRef = ref<HTMLElement>()

// État de la rotation automatique 360°
const isAutoRotating = ref(false)
const autoRotationInterval = ref<number | null>(null)

// Composable de préchargement optimisé
const {
  loadProgress,
  isLoading,
  // loadedAngles,
  isAngleLoaded,
  startPreload,
  cancelPreload
} = useImagePreloader360({
  numAngleStep: props.numAngleStep,
  materials: props.materials,
  materialDirPath: props.materialDirPath,
  numAngles: props.numAngles,
  initialAngle: props.initialAngle,
  maxConnections: 8
})

// Composable de rotation avec limitation aux angles chargés
const {
  currentAngle,
  isRotating,
  setAngle,
  nextAngle,
  prevAngle,
  bindEvents,
  unbindEvents
} = useRotation360({
  numAngleStep: props.numAngleStep,
  disabled: isLoading,
  onAngleChange: (angle) => {
    // Vérifier si l'angle est chargé avant d'émettre
    if (isAngleLoaded(angle)) {
      emit('angleChange', angle)
    }
  }
})

/**
 * Calculer le pas d'angle (espacement entre les images)
 */
const angleStep = computed(() => Math.round(props.numAngles / props.numAngleStep))

/**
 * Générer le chemin source d'une image
 *
 * @param angleIdx - Index de l'angle (0 à numAngleStep-1)
 * @param material - Identifiant du matériau
 * @param isShadow - Si true, génère le chemin pour une ombre
 * @returns Chemin complet de l'image
 */
const getImageSrc = (angleIdx: number, material: string, isShadow: boolean = false): string => {
  if (isShadow) {
    // Chemin pour les ombres
    const imageIndex = angleIdx * angleStep.value + 1
    const paddedIndex = String(imageIndex).padStart(2, '0')
    return `${props.materialDirPath}EXT-shadows/shadow_${paddedIndex}.png`
  }

  // Calculer l'index de l'image réelle
  const imageIndex = angleIdx * angleStep.value + 1

  // Formater l'index sur 2 chiffres (01, 02, ..., 36)
  const paddedIndex = String(imageIndex).padStart(2, '0')

  // Toujours utiliser PNG pour tous les layers (y compris la base)
  // afin de supporter les images avec transparence
  const extension = 'png'

  return `${props.materialDirPath}${material}/${material}_${paddedIndex}.${extension}`
}

/**
 * Calculer le z-index d'une layer
 * Formule: 100 * angleIndex + layer
 * Pour les ombres, on utilise un z-index négatif
 * 
 * @param angleIdx - Index de l'angle
 * @param layer - Numéro de layer
 * @param isShadow - Si true, c'est une couche d'ombre
 * @returns Valeur de z-index
 */
const getZIndex = (angleIdx: number, layer: number, isShadow: boolean = false): number => {
  if (isShadow) {
    // Les ombres sont sous toutes les autres couches
    return -100 * (angleIdx + 1)
  }
  return 100 * angleIdx + layer
}

/**
 * Créer une clé unique pour une image
 */
const getImageKey = (angleIdx: number, material: string): string => {
  return `${angleIdx}-${material}`
}

/**
 * Vérifier si un angle est actif
 */
const isAngleActive = (angleIdx: number): boolean => {
  return currentAngle.value === angleIdx
}

/**
 * Déclencher une rotation complète 360° automatique
 */
const start360Rotation = (): void => {
  if (isAutoRotating.value) {
    return // Déjà en cours
  }

  isAutoRotating.value = true
  let rotationCount = 0
  const totalRotations = props.numAngleStep

  const rotateStep = () => {
    if (rotationCount >= totalRotations) {
      // Rotation terminée
      stop360Rotation()
      return
    }

    prevAngle()
    rotationCount++

    // Programmer la prochaine étape (200ms entre chaque angle)
    autoRotationInterval.value = setTimeout(rotateStep, 50)
  }

  // Démarrer la rotation
  rotateStep()
}

/**
 * Arrêter la rotation automatique 360°
 */
const stop360Rotation = (): void => {
  if (autoRotationInterval.value) {
    clearTimeout(autoRotationInterval.value)
    autoRotationInterval.value = null
  }
  isAutoRotating.value = false
}

/**
 * Lifecycle hooks
 */
onMounted(async () => {
  // Définir l'angle initial
  setAngle(props.initialAngle)
  
  // Attacher les événements de rotation
  if (viewerRef.value) {
    bindEvents(viewerRef.value)
  }
  
  // Démarrer le préchargement optimisé
  await startPreload()
})

onUnmounted(() => {
  // Détacher les événements
  unbindEvents()

  // Annuler le préchargement en cours
  cancelPreload()

  // Arrêter la rotation automatique
  stop360Rotation()
})

// Watcher pour la progression du chargement
watch(loadProgress, (progress) => {
  emit('loadProgress', progress)
  
  // Émettre l'événement de completion à 100%
  if (progress >= 100) {
    emit('loadComplete')
  }
})

// Watcher pour les changements de matériaux
watch(() => props.materials, async () => {
  // Annuler le préchargement en cours
  cancelPreload()

  // Redémarrer le préchargement
  await startPreload()
}, { deep: true })

// Watcher pour déclencher la rotation 360°
watch(() => props.trigger360Rotation, (newValue) => {
  if (newValue) {
    start360Rotation()
  }
})

// Exposer les méthodes publiques pour le composant parent
defineExpose({
  setAngle,
  nextAngle,
  prevAngle,
  start360Rotation,
  stop360Rotation
})
</script>

<template>
  <div ref="viewerRef" class="vue360-viewer" :class="{ 'is-rotating': isRotating, 'is-loading': isLoading }">
    <!-- Stage de rotation -->
    <div class="vue360-stage">
      <!-- Chaque angle (affichage conditionné au chargement) -->
      <div
        v-for="angleIdx in numAngleStep"
        :key="angleIdx - 1"
        :class="['vue360-angle', { 'is-active': isAngleActive(angleIdx - 1), 'is-loaded': isAngleLoaded(angleIdx - 1) }]"
      >
        <!-- Afficher les layers seulement si l'angle est chargé -->
        <template v-if="isAngleLoaded(angleIdx - 1)">
          <!-- Layer d'ombre (s'affiche sous le véhicule) -->
          <img
            :src="getImageSrc(angleIdx - 1, '', true)"
            alt=""
            class="vue360-layer vue360-shadow"
            draggable="false"
          />
          <!-- Layers des matériaux du véhicule -->
          <img
            v-for="mat in materials"
            :key="getImageKey(angleIdx - 1, mat.material)"
            :src="getImageSrc(angleIdx - 1, mat.material)"
            :style="{ zIndex: getZIndex(angleIdx - 1, mat.layer) }"
            :alt="`${mat.material} - angle ${angleIdx - 1}`"
            class="vue360-layer"
            draggable="false"
          />
        </template>
      </div>
    </div>

    <!-- Contrôles de navigation -->
    <ViewerControls
      v-if="showControls"
      :current-angle="currentAngle"
      :num-angle-step="numAngleStep"
      :is-angle-loaded="isAngleLoaded"
      :disabled="isLoading"
      @prev="prevAngle"
      @next="nextAngle"
      @go-to-angle="setAngle"
    />

    <!-- Indicateur de chargement -->
    <div v-if="isLoading" class="vue360-loading">
      <LoadingSpinner size="large" />
      <span class="loading-text">{{ Math.round(loadProgress) }}%</span>
    </div>
  </div>
</template>

<style scoped>
.vue360-viewer {
  position: relative;
  width: 100%;
  max-width: 875px;
  height: auto;
  max-height: 525px;
  aspect-ratio: 875 / 525;
  overflow: hidden;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

.vue360-viewer.is-rotating {
  cursor: grabbing;
  cursor: -webkit-grabbing;
}

.vue360-viewer:not(.is-rotating):not(.is-loading) {
  cursor: grab;
  cursor: -webkit-grab;
}

.vue360-viewer.is-loading {
  cursor: default;
}

.vue360-stage {
  position: relative;
  width: 100%;
  height: 100%;
}

.vue360-angle {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  visibility: hidden;
  opacity: 0;
}

.vue360-angle.is-active {
  visibility: visible;
  opacity: 1;
}

.vue360-layer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  pointer-events: none;
  -webkit-user-drag: none;
}

/* Layer d'ombre portée - s'affiche sous le véhicule */
.vue360-shadow {
  z-index: 0;
  /* Positionner l'ombre en bas de la scène */
  bottom: 0;
  top: auto;
  /* Masquer si l'image ne charge pas */
  /* opacity: 0.9; */
}

.vue360-loading {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-md);
  background-color: rgba(26, 26, 26, var(--opacity-overlay));
  z-index: var(--z-indicator);
  pointer-events: none;
  backdrop-filter: blur(1px);
}

.loading-text {
  font-family: var(--font-family-base);
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-primary);
}


/* Optimisations mobiles */
@media (max-width: 768px) {
  .vue360-viewer {
    touch-action: none;
    -webkit-touch-callout: none;
  }
}
</style>